import {AxiosError, AxiosResponse} from "axios"
import {injectable} from "inversify"

import "@/extensions/date.extensions"
import {ApiService} from "@/services/apiService"
import {IMemberService} from "@/injection/interfaces"
import {IAuthenticatedMember} from "@/types/entities/authenticatedMember"
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse"
import {IAddMemberRequest} from "@/types/requests/addMemberRequest"
import {Member} from "@/types/entities/member"
import {useMemberStore} from "@/stores/memberStore"
import {ActivityRegistration, Role} from "@/types"

@injectable()
export class MemberService extends ApiService implements IMemberService {
  public async getCurrentMember(): Promise<IAuthenticatedMember | undefined> {
    try {
      const response = await this
        ._httpClient
        .get<IAuthenticatedMember, AxiosResponse<IAuthenticatedMember>>(`${process.env.VUE_APP_API_BASE_URL}/api/members/me`)
      return response.data
    } catch (error) {
      return Promise.reject(error)
    }
  }

  public async getMember(organizationId: string, memberId: string): Promise<Member> {
    const memberStore = useMemberStore()
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/members/${memberId}`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/members/${memberId}`
    const response = await this
      ._httpClient
      .get<AxiosResponse<Member>>(endpointUrl)
      .catch(function (error: AxiosError): AxiosResponse<Member> {
        return error.response as AxiosResponse<Member>
      })
    return response.data as Member
  }

  public async addMember(request: IAddMemberRequest, organizationId: string): Promise<SucceededOrNotResponse> {
    const memberStore = useMemberStore()
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/members`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/members`
    const response = await this._httpClient
      .post<IAddMemberRequest, AxiosResponse<any>>(endpointUrl, request)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>
      })
    const succeededOrNotResponse = response.data as SucceededOrNotResponse
    return new SucceededOrNotResponse(succeededOrNotResponse.succeeded, succeededOrNotResponse.errors)
  }

  public async updateMember(member: Member, organizationId: string): Promise<SucceededOrNotResponse> {
    const memberStore = useMemberStore()
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/members/${member.id}`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/members/${member.id}`
    const response = await this
      ._httpClient
      .put<Member, AxiosResponse<any>>(endpointUrl, member)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>
      })
    const succeededOrNotResponse = response.data as SucceededOrNotResponse
    return new SucceededOrNotResponse(succeededOrNotResponse.succeeded, succeededOrNotResponse.errors)
  }

  public async updateAuthenticatedMember(request: IAuthenticatedMember): Promise<SucceededOrNotResponse> {
    try {
      const response = await this
        ._httpClient
        .put<IAuthenticatedMember, AxiosResponse<SucceededOrNotResponse>>(
          `${process.env.VUE_APP_API_BASE_URL}/api/members/me`,
          request)
      const succeededOrNotResponse = response.data as SucceededOrNotResponse
      return new SucceededOrNotResponse(succeededOrNotResponse.succeeded, succeededOrNotResponse.errors)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  public async deleteMember(memberId: string, organizationId: string): Promise<SucceededOrNotResponse> {
    const memberStore = useMemberStore()
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/members/${memberId}`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/members/${memberId}`
    const response = await this
      ._httpClient
      .delete<AxiosResponse<any>>(endpointUrl)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>
      })
    return new SucceededOrNotResponse(response.status === 204)
  }

  public async getCurrentMemberActivityRegistrations(): Promise<ActivityRegistration[] | SucceededOrNotResponse> {
    const response = await this._httpClient
      .get<ActivityRegistration[], AxiosResponse<ActivityRegistration[]>>(`${process.env.VUE_APP_API_BASE_URL}/api/members/me/my-activity-registrations`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as ActivityRegistration[];
  }

  public async deleteCurrentMemberActivityRegistration(activityRegistrationId: string): Promise<SucceededOrNotResponse> {
    const response = await this._httpClient.delete<string, AxiosResponse<SucceededOrNotResponse>>(`${process.env.VUE_APP_API_BASE_URL}/api/members/me/activity-registrations/${activityRegistrationId}`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return new SucceededOrNotResponse(response.status === 204)
  }

  public async deleteCurrentMemberActivityRegistrationParticipant(activityRegistrationId: string, participantId: string): Promise<SucceededOrNotResponse> {
    const response = await this._httpClient.delete<string, AxiosResponse<SucceededOrNotResponse>>(`${process.env.VUE_APP_API_BASE_URL}/api/members/me/activity-registrations/${activityRegistrationId}/participants/${participantId}`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return new SucceededOrNotResponse(response.status === 204)
  }
}